import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { DocumentNode } from 'graphql';

import { useCustomMemo } from 'common/helpers/useCustomMemo';

import type { SourceProps } from '../Source';
import useStaleEntities from '../useStaleEntities/useStaleEntities';
import useWsReconnectRefetch from '../useWsReconnectRefetch/useWsReconnectRefetch';
import useEntityListWatcher from '../utils/EntityListMatcher/useEntityListWatcher';
import useStoreHelper from '../utils/WithStoreHelpers/useStoreHelper';
import useCreateSubscriptions from './useCreateSubscriptions';
import useDeleteSubscriptions from './useDeleteSubscriptions';
import useUpdateSubscriptions from './useUpdateSubscriptions';

export type SubscriptionDescriptor = { query: DocumentNode; name?: string; variables?: Record<string, unknown> };

export type ReactiveSourceProps<EntityResponse, QueryResponse> = SourceProps<EntityResponse, QueryResponse>;

function ReactiveSource<EntityResponse, QueryResponse>({
  subscriptions,
  query,
  skip: skipQuery,
  selectEntities,
  selectEntityId,
  selectSyncOn,
  name,
  selectStaleEntities,
  onEntityLoaded,
  onEntityCreated,
  onEntityDeleted,
  onEntityUpdated,
  children,
  queryVariables: propsQueryVariables,
}: React.PropsWithChildren<ReactiveSourceProps<EntityResponse, QueryResponse>>) {
  const queryVariables = useCustomMemo({
    ...propsQueryVariables,
    fetch: true,
    sync: false,
  });

  const queryResult = useQuery<QueryResponse>(query, {
    variables: queryVariables,
    skip: skipQuery,
  });

  const { loading: queryLoading, data } = queryResult;

  useWsReconnectRefetch(queryResult);

  // stale entities represent entities with latest state synced from original query
  // stale entities are no longer stale once subscription updates its state, so it does not longer have to be synced
  const { markAsStale, markAsUpToDate } = useStaleEntities<EntityResponse, QueryResponse>({
    selectStaleEntities,
    selectEntityId,
    query,
  });

  const { subscribe: subscribeToCreate } = useCreateSubscriptions({
    subscriptions: subscriptions.create,
    queryResult,
    selectEntityId,
    selectEntities,
  });

  const { subscribe: subscribeToDelete, unsubscribeFrom: unsubscribeFromDelete } = useDeleteSubscriptions({
    subscription: subscriptions.delete,
    queryResult,
    onSubscribe: markAsStale,
    selectEntityId,
    selectEntities,
  });

  const { subscribe: subscribeToUpdate, unsubscribeFrom: unsubscribeFromUpdate } = useUpdateSubscriptions({
    subscription: subscriptions.update,
    queryResult,
    onSubscribe: markAsStale,
    selectEntityId,
    selectEntities,
    selectSyncOn,
  });

  const storeHelpers = useStoreHelper<EntityResponse, QueryResponse>({
    selectEntityId,
    selectSyncOn,
    name,
    loading: queryLoading,
    selectEntities,
    query,
    queryVariables,
  });

  const handleEntityLoaded = useCallback(
    (newEntity: EntityResponse) => {
      subscribeToUpdate(newEntity);
      subscribeToDelete(newEntity);
      onEntityLoaded?.(newEntity, storeHelpers);
    },
    [subscribeToUpdate, subscribeToDelete, onEntityLoaded, storeHelpers]
  );

  // when entity is created, subscribe to its update and delete events, and execute callback from parent component
  const handleEntityCreated = useCallback(
    (newEntity: EntityResponse) => {
      subscribeToUpdate(newEntity);
      subscribeToDelete(newEntity);
      onEntityCreated?.(newEntity, storeHelpers);
    },
    [subscribeToUpdate, subscribeToDelete, onEntityCreated, storeHelpers]
  );

  // when entity is updated, mark entity as up to date and execute callback from parent component
  const handleEntityUpdated = useCallback(
    (currEntity: EntityResponse, prevEntity: EntityResponse) => {
      markAsUpToDate(currEntity);
      onEntityUpdated?.(currEntity, prevEntity, storeHelpers);
    },
    [markAsUpToDate, onEntityUpdated, storeHelpers]
  );

  // when entity is deleted, unsubscribe to its update and delete events, and execute callback from parent component
  const handleEntityDeleted = useCallback(
    (deletedEntity: EntityResponse) => {
      unsubscribeFromUpdate(selectEntityId(deletedEntity));
      unsubscribeFromDelete(selectEntityId(deletedEntity));
      onEntityDeleted?.(deletedEntity, storeHelpers);
    },
    [unsubscribeFromUpdate, unsubscribeFromDelete, onEntityDeleted, storeHelpers, selectEntityId]
  );

  const entities = useMemo(() => selectEntities(data), [data, selectEntities]);
  const staleEntities = useMemo(() => selectStaleEntities(data), [data, selectStaleEntities]);

  useEntityListWatcher({
    name,
    skip: skipQuery,
    loading: queryResult.loading,
    staleEntities,
    entities,
    selectEntityId,
    selectSyncOn,
    onEntityLoaded: handleEntityLoaded,
    onEntityCreated: handleEntityCreated,
    onEntityUpdated: handleEntityUpdated,
    onEntityDeleted: handleEntityDeleted,
  });

  // subscribe will happen only the first time when queryLoading becomes false
  if (!skipQuery && !queryLoading) {
    subscribeToCreate();
  }

  return <>{children(entities, storeHelpers, queryResult)}</>;
}

export default React.memo(ReactiveSource) as typeof ReactiveSource;
